import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Loader2, Pencil } from "lucide-react";
import {
  useAnchoringDocuments,
  type AnchoredDocumentRow,
} from "~/api/v2/useAnchoringDocuments";
import { useBulkSelection } from "~/hooks/useBulkSelection";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import { CenterSpinner } from "~/atoms/spinner";
import ErrorState from "~/molecules/ErrorState";
import { AnchoringTable, type AnchoringColumn } from "./AnchoringTable";
import InviteQuizTakersModal from "./InviteQuizTakersModal";
import { quizCreatorColumns, releaseColumns, titleColumn } from "./cells";

export const AnchoredDocumentsTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { anchored, isLoading, error, mutate } = useAnchoringDocuments();
  const selection = useBulkSelection(
    anchored.filter((row) => row.status === "ready").map((row) => row.id),
  );
  const [isInviteOpen, setInviteOpen] = useState(false);

  if (isLoading) return <CenterSpinner />;
  if (error) return <ErrorState />;

  const selectedQuizzes = anchored
    .filter((row) => selection.selectedIds.has(row.id))
    .map((row) => ({ quizId: row.id, category: row.category }));

  const columns: AnchoringColumn<AnchoredDocumentRow>[] = [
    {
      key: "edit",
      header: "",
      cell: (row) =>
        row.status === "generating" ? (
          <Badge variant="secondary">
            <Loader2 data-icon="inline-start" className="animate-spin" />
            {t(($) => $.anchoring.manage.creating)}
          </Badge>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={(event) => {
              event.stopPropagation();
              void navigate(`/anchoring/${row.id}?mode=edit`);
            }}
          >
            <Pencil data-icon="inline-start" />
            {t(($) => $.anchoring.manage.edit)}
          </Button>
        ),
    },
    titleColumn(t, "quiz"),
    {
      key: "takers",
      header: t(($) => $.anchoring.columns.takers),
      cell: (row) =>
        row.status === "generating" ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          `${row.completedCount} / ${row.invitedCount}`
        ),
    },
    ...quizCreatorColumns(t),
    ...releaseColumns(t),
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          variant="outline"
          disabled={selection.selectedIds.size === 0}
          onClick={() => setInviteOpen(true)}
        >
          {t(($) => $.anchoring.actions.inviteTakers)}
        </Button>
      </div>
      <AnchoringTable
        columns={columns}
        rows={anchored}
        getRowId={(row) => row.id}
        emptyMessage={t(($) => $.anchoring.empty.anchored)}
        selection={selection}
        isRowSelectable={(row) => row.status === "ready"}
        onRowClick={(row) => {
          if (row.status === "generating") return;
          void navigate(`/anchoring/${row.id}`);
        }}
      />
      {isInviteOpen && (
        <InviteQuizTakersModal
          quizzes={selectedQuizzes}
          onOpenChange={setInviteOpen}
          onInvited={() => {
            selection.clearSelection();
            void mutate();
          }}
        />
      )}
    </div>
  );
};
